import { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react';
import { IFormField } from '@vivli/shared/infrastructure/interface';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { BaseInputFieldComponent } from '../input/base-input-field.component';
import { FormFieldTypeEnum } from '@vivli/shared/infrastructure/enum';
import mergeRefs from 'react-merge-refs';
import { ISelectOption } from '@vivli/features/search/infrastructure/interface';
import { AdvancedSelectComponent } from '@vivli/shared/components';

const displayStyle: CSSProperties = {
    position: 'relative',
    paddingTop: 0,
    paddingBottom: 0,
    border: 'none',
    fontSize: Size.FontSize.Large,
    color: Color.DARK_GRAY,
    ...Styles.borderRadius(Size.BORDER_RADIUS),
    ...Styles.NO_SELECT,
};

interface AdvancedSelectFieldComponentProps extends IFormField {
    isMulti?: boolean;
    isSearchable?: boolean;
    isClearable?: true;
    className?: string;
    title?: string;
    onChange: (selectedOptions: ISelectOption[]) => void;
    onBlur?: (e: any) => void;
    options: ISelectOption[];
    selectedOptions?: ISelectOption[];
    targetStyle?: CSSProperties;
    fieldStyle?: CSSProperties;
    isDisabled?: boolean;
    sort?: 'asc' | 'desc';
    menuPosition?: 'fixed' | 'absolute';
    menuPlacement?: 'auto' | 'bottom' | 'top';
    defaultValue?: string;
    readOnly?: boolean;
}

export const AdvancedSelectFieldComponent = forwardRef((props: AdvancedSelectFieldComponentProps, ref) => {
    const selectRef = useRef<HTMLDivElement>();
    const [activeItems, setActiveItems] = useState<ISelectOption[]>([]);
    const [selectedItems, setSelectedItems] = useState<ISelectOption[]>(null);
    const {
        isMulti = false,
        isSearchable,
        isClearable,
        className,
        onChange,
        options,
        selectedOptions,
        targetStyle,
        fieldStyle,
        isDisabled,
        sort,
        menuPosition = 'fixed',
        menuPlacement = 'auto',
        defaultValue,
        readOnly,
        label,
        onBlur,
    } = props;
    const handleOnChange = (selectedItems: ISelectOption[]) => {
        onChange && onChange(selectedItems);
        setSelectedItems(selectedItems);
    };

    const getItem = (comparisonValue: string) => {
        return activeItems?.find((i) => i.value === comparisonValue);
    };

    useEffect(() => {
        if ((!selectedOptions || selectedOptions.length == 0) && defaultValue) {
            const item = getItem(defaultValue);
            setSelectedItems([item]);
        }
    }, []);

    /* Needs to check if the length of the two lists has changed to prevent
     *  redundant re-rendering when in use with areas like BulkUploadSelectors */
    useEffect(() => {
        if (!options || options.length === activeItems?.length) {
            return;
        }
        setActiveItems(options);
    }, [options]);

    const buildInput = (_props) => (
        <AdvancedSelectComponent
            options={options}
            selectedOptions={selectedOptions}
            onChange={handleOnChange}
            onBlur={onBlur}
            isMulti={isMulti}
            isSearchable={isSearchable}
            isClearable={isClearable}
            targetStyle={{
                ...targetStyle,
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'white',
            }}
            fieldStyle={fieldStyle}
            isDisabled={isDisabled || readOnly}
            className={className}
            sort={sort}
            menuPosition={menuPosition}
            menuPlacement={menuPlacement}
        />
    );

    const inputDisplay = buildInput({ defaultValue });
    const buildDisplayOnly = () => (
        <div style={displayStyle}>
            <ul>
                {selectedOptions?.map((item) => (
                    <li key={item.value}>{item.label}</li>
                ))}
            </ul>
        </div>
    );

    const readonlyDisplay = buildDisplayOnly();

    const buildDisplayOnlyDebug = selectedOptions?.map((item) => 'key=${item.value}-${item.label}').join(', ');

    return (
        <BaseInputFieldComponent {...props} type={FormFieldTypeEnum.DropDown} inputRef={selectRef} label={label || ''}>
            <div ref={mergeRefs([selectRef, ref])}>{!readOnly ? inputDisplay : readonlyDisplay}</div>
        </BaseInputFieldComponent>
    );
});
